import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { COMPANY_INFO, SITE_IMAGES, getCdnImage } from '../data/content';

export function ForPrivate() {
  const [openFaq, setOpenFaq] = useState(0);

  const toggleFaq = (idx) => {
    setOpenFaq(prev => (prev === idx ? null : idx));
  };
  const FAQ_PRIVATE = [
    {
      q: 'Hvad er SYSTEM 3E præcist?',
      a: 'Det er et patenteret modulært vægsystem lavet af det naturlige mineral perlit. Tænk på det som præcise byggeklodser, der klikker sammen uden brug af mørtel, lim eller vand.',
    },
    {
      q: 'Kan SYSTEM 3E bruges til mit nye hus?',
      a: 'Ja, perfekt til parcelhuse, sommerhuse, tilbygninger og garageanlæg. Systemet udgør både husets stærke bærende ydervægge og den høje isolering i én samlet arbejdsgang.',
    },
    {
      q: 'Skal jeg selv bygge, eller kan min håndværker bruge det?',
      a: 'Begge dele! Mange private vælger at lade deres lokale tømrer, murer eller entreprenør opføre råhuset. Da Click-Click systemet er så intuitivt, tager det ingen tid for fagmanden at lære.',
    },
    {
      q: 'Hvorfor er det bedre for mit indeklima?',
      a: 'Da der ikke tilføres litervis af vand via våd mørtel under muringen, er væggene helt tørre fra dag ét. Du undgår risiko for skimmelsvamp og indbygget fugt.',
    },
    {
      q: 'Hvordan kommer jeg i gang?',
      a: 'Send dine tegninger eller idéer til AGT Nordic Group ApS. Vi hjælper med at beregne det nøjagtige elementforbrug og giver dig et uforpligtende tilbud på levering.',
    },
    {
      q: 'Hvem kontakter jeg i Danmark?',
      a: `AGT Nordic Group ApS i Vordingborg er den eksklusive eneforhandler i Danmark. Du kontakter os direkte på tlf. ${COMPANY_INFO.phone} eller e-mail ${COMPANY_INFO.email}.`,
    },
  ];

  return (
    <>
      <SEO
        title="For private bygherrer – SYSTEM 3E"
        description="Byg dit drømmehus hurtigere, tørrere og mere energieffektivt med SYSTEM 3E. Læs hvordan du som privatbygherre får et sundt indeklima og en tryg byggeproces."
      />

      {/* Hero */}
      <section className="bg-[#243447] text-white py-16 sm:py-24 relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={getCdnImage(SITE_IMAGES.modernHouse, 1600, 80)}
            alt="Moderne villabyggeri med SYSTEM 3E"
            className="w-full h-full object-cover opacity-45"
          />
          <div className="absolute inset-0 bg-[#162332]/45" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#819FA8] font-extrabold text-xs uppercase tracking-widest block mb-2">
              Til Dig Der Skal Bygge Hus
            </span>
            <h1 className="text-3xl sm:text-5xl font-black text-white tracking-tight mb-6">
              Drømmer du om at bygge nyt hus?
            </h1>
            <p className="text-slate-300 text-base sm:text-lg leading-relaxed mb-8">
              Oplev en enklere og hurtigere byggeproces. SYSTEM 3E giver dig et supersundt indeklima, minimalt energiforbrug og fuld tryghed fra start til slut.
            </p>

            <NavLink
              to="/kontakt"
              className="bg-[#122a4e] hover:bg-[#1d3b66] text-white font-bold px-8 py-4 rounded-xl text-base transition-all inline-flex items-center gap-2 shadow-lg"
            >
              <span>Fortæl os om dit byggeprojekt</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </NavLink>
          </div>
        </div>
      </section>

      {/* Key Private Benefits */}
      <section className="py-16 sm:py-24 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-[#122a4e] font-bold text-xs uppercase tracking-widest block mb-2">
              Tryg Byggeproces
            </span>
            <h2 className="text-3xl font-extrabold text-slate-900 tracking-tight">
              Hvorfor vælge SYSTEM 3E til dit nye hjem?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-slate-50 border border-slate-200 rounded-3xl p-8 flex flex-col justify-between">
              <div>
                <div className="w-12 h-12 bg-[#122a4e]/10 text-[#122a4e] rounded-2xl flex items-center justify-center text-xl mb-6">
                  <i className="fa-solid fa-temperature-arrow-down" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Lav el- & varmeregning</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Perlitens høje isoleringsevne (U-værdi 0,198) holder på varmen om vinteren og sikrer behagelig kølighed om sommeren.
                </p>
              </div>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-3xl p-8 flex flex-col justify-between">
              <div>
                <div className="w-12 h-12 bg-[#122a4e]/10 text-[#122a4e] rounded-2xl flex items-center justify-center text-xl mb-6">
                  <i className="fa-solid fa-wind" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Sundt Indeklima</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Ingen fugt i muren fra mørtel. Det rene mineralmateriale forebygger skimmelsvamp og giver et tørt, behageligt indeklima.
                </p>
              </div>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-3xl p-8 flex flex-col justify-between">
              <div>
                <div className="w-12 h-12 bg-[#122a4e]/10 text-[#122a4e] rounded-2xl flex items-center justify-center text-xl mb-6">
                  <i className="fa-solid fa-clock-rotate-left" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Hurtigere indflytning</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Råhuset rejses på rekordtid. Det betyder kortere byggeperiode, færre renteudgifter til realkreditlån og hurtigere indflytning.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Private FAQ Section */}
      <section className="py-16 sm:py-24 bg-slate-50 border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#122a4e] font-bold text-xs uppercase tracking-widest block mb-2">
              Spørgsmål & Svar
            </span>
            <h2 className="text-2xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
              Ofte stillede spørgsmål fra private bygherrer
            </h2>
          </div>

          <div className="space-y-3">
            {FAQ_PRIVATE.map((item, idx) => {
              const isOpen = openFaq === idx;
              return (
                <div
                  key={idx}
                  className="bg-white border border-slate-200 rounded-2xl overflow-hidden transition-all duration-300 shadow-sm hover:border-[#5A6E75]/40"
                >
                  <button
                    type="button"
                    onClick={() => toggleFaq(idx)}
                    className="w-full text-left p-5 sm:p-6 flex items-center justify-between gap-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5A6E75] cursor-pointer"
                    aria-expanded={isOpen}
                  >
                    <span className="font-bold text-slate-900 text-base flex items-center gap-3">
                      <i className="fa-solid fa-circle-question text-[#5A6E75] text-lg shrink-0" aria-hidden="true" />
                      <span>{item.q}</span>
                    </span>
                    <div className={`w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180 bg-[#5A6E75]/10 text-[#5A6E75]' : 'text-slate-500'}`}>
                      <i className="fa-solid fa-chevron-down text-xs" aria-hidden="true" />
                    </div>
                  </button>

                  <div
                    className={`grid transition-all duration-300 ease-in-out ${
                      isOpen ? 'grid-rows-[1fr] opacity-100 pb-5 sm:pb-6 px-5 sm:px-6' : 'grid-rows-[0fr] opacity-0 px-5 sm:px-6'
                    }`}
                  >
                    <div className="overflow-hidden">
                      <p className="text-slate-600 text-sm leading-relaxed pl-7 border-l-2 border-[#5A6E75]/20">
                        {item.a}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Sole Distributor Private CTA */}
      <section className="py-16 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-extrabold mb-4">
            Kontakt din danske eneforhandler i dag
          </h2>
          <p className="text-slate-300 text-sm sm:text-base max-w-2xl mx-auto mb-8">
            Vi sidder klar i Vordingborg til at hjælpe dig videre med rådgivning, beregning og tilbud til dit drømmebyggeri.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <NavLink
              to="/kontakt"
              className="bg-[#122a4e] hover:bg-[#1d3b66] text-white font-bold px-8 py-4 rounded-xl text-base transition-all shadow-lg"
            >
              Fortæl os om dit byggeprojekt
            </NavLink>
            <a
              href={COMPANY_INFO.phoneRaw}
              className="bg-slate-800 hover:bg-slate-700 text-white font-bold px-8 py-4 rounded-xl text-base transition-all border border-slate-700 whitespace-nowrap"
            >
              Ring på {COMPANY_INFO.phone}
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
